/**
 * Web ApiAdapter — constructs the HttpClient with browser `fetch` +
 * token-provider, then wires the per-entity API classes.
 *
 * CP1: shared code cannot touch `window.fetch` or `localStorage`. This file
 * lives in apps/web precisely because this is where those globals are
 * allowed. The orchestrators receive the already-wired ApiAdapter.
 *
 * T7 moves the sole `localStorage` access point into
 * `apps/web/src/auth/tokenStorage.ts` (legacy key names `sessionToken` +
 * `user`). This adapter no longer hosts its own TokenProvider impl — when
 * `options.tokens` is omitted we fall back to `makeTokenStorage()` from the
 * auth barrel so the HttpClient always has a sane provider.
 *
 * That keeps grep-audits simple: the string `localStorage` appears only in
 * `apps/web/src/auth/tokenStorage.ts` (+ comments / tests).
 */

import {
  AuthApi,
  HouseholdsApi,
  HttpClient,
  HouseholdProductsApi,
  ShoppingListsApi,
  RetailerOffersApi,
  FlyerDealsApi,
  StoresApi,
  type TokenProvider,
} from '@shopista/shared/api';
import type { ApiAdapter } from '@shopista/shared/stores';
import { makeTokenStorage } from '../auth/tokenStorage';
import { makeWebRefreshLock } from '../lib/webRefreshLock';
import { toFetchAdapter } from '../lib/fetchAdapter';

export interface CreateWebApiAdapterOptions {
  /** Absolute base URL, e.g. `http://localhost:3001`. No trailing slash. */
  baseUrl: string;
  /**
   * Optional override for the token provider. Defaults to
   * `makeTokenStorage()` which reads/writes the legacy `sessionToken` /
   * `user` keys in `localStorage`.
   */
  tokens?: TokenProvider;
}

export type WebApiAdapter = ApiAdapter & { http: HttpClient };

export function createWebApiAdapter(
  options: CreateWebApiAdapterOptions
): WebApiAdapter {
  const tokens = options.tokens ?? makeTokenStorage();
  const http = new HttpClient({
    baseUrl: options.baseUrl,
    fetch: toFetchAdapter(window.fetch.bind(window)),
    tokens,
    withCredentials: true,
    // BUG15: serialize /api/auth/refresh per-origin across this client, the
    // sync engine's client, and every tab via one shared Web Locks name.
    lock: makeWebRefreshLock(),
  });
  return {
    http,
    auth: new AuthApi(http),
    households: new HouseholdsApi(http),
    ShoppingLists: new ShoppingListsApi(http),
    HouseholdProducts: new HouseholdProductsApi(http),
    RetailerOffers: new RetailerOffersApi(http),
    FlyerDeals: new FlyerDealsApi(http),
    stores: new StoresApi(http),
  };
}
